<template>
  <DialogProfileItemTemplate
    :disabled="isCurrentMember"
    :has-details="addDetails"
    :class="{
      selected: isCurrentMember,
      disabled: isCurrentMember && !isCurrentMemberClickabled,
    }"
  >
    <template #image>
      <component :is="tag" class="button" @click="handleClickSelect">
        <ImgLoad
          class="avatar-image"
          effect="none"
          alt="avatar"
          :width="300"
          :height="300"
          :src="member.avatarUrl"
        />
      </component>
    </template>
    <template #label>
      <icon v-if="showWarning" name="warning" size="2rem" color="primary" />
      <span class="label-name">{{ member.firstname || member.name }}</span>
    </template>
    <template #details>
      <span v-if="addDetails" class="details">{{ details || `&nbsp;` }}</span>
    </template>
  </DialogProfileItemTemplate>
</template>
<script setup lang="ts">
interface Props {
  member: FamilyMember;
  isCurrentMemberClickabled?: boolean;
  addDetails?: boolean;
}

const props = defineProps({
  member: null,
  isCurrentMemberClickabled: { type: Boolean, default: false },
  addDetails: { type: Boolean, default: true }
});

const emit = defineEmits(["select"]);

const context = useContextStore();

const profile = useProfileStore();

const {
  shareResultsWithFamily,
  details: childDetails,
  status,
} = useFamilyMember(props.member);

const isCurrentMember = computed(
  () => props.member.memberId === context.memberId
);

const showWarning = computed(() => {
  if (isCurrentMember.value) return false;
  if (!profile.isASubscriber) return true;
  if (!shareResultsWithFamily.value) return true;
  return false;
});

const tag = computed(() =>
  isCurrentMember.value && !props.isCurrentMemberClickabled ? 'span' : 'button'
);

const handleClickSelect = () => {
  emit('select', props.member);
};

const details = computed(() => {
  if (status.value === 'parent') return 'Parent';
  return childDetails;
});
</script>
<style lang="scss" scoped>
.icon {
  position: relative;
  top: -0.2rem;
  left: -0.2rem;
  color: var(--color_secondary);
}

.button {
  position: relative;

  :deep(img) {
    position: relative;
    z-index: 1;
  }

  .avatar-image {
    transition: transform 300ms $ease-out-quad;
  }

  &::before {
    content: '';
    position: absolute;
    top: -0.4rem;
    left: -0.4rem;
    width: calc(100% + 0.8rem);
    height: calc(100% + 0.8rem);
    border-radius: 50%;
    opacity: 0;
    background-color: var(--color_primary);
  }

  width: 100%;
  height: 100%;
  transition: transform 300ms $ease-out-back;

  &:hover,
  &:focus {
    .avatar-image {
      transform: scale(1.1);
      transition: transform 400ms $ease-out-back;
    }
  }
}

.label-name {
  @include font('m', 400, 'secondary');

  color: var(--color_neutral_900);
}

.disabled {
  .button {
    transform: scale(1);
    border: 0.7rem solid var(--color_secondary_darker);
    border-radius: 50%;
    .avatar-image {
      transform: none;
      transition: none;
    }
  }
}

.selected {
  .label-name {
    font-weight: 700;
  }
}
</style>
